import i18nextConfig from "../../../../next-i18next.config";
import { TranslationFileName } from "../translationFiles";

import { TRANSLATION_BUNDLE_IMPORTS } from "./translationBundles";

type TranslationBundle = Record<string, unknown>;

export interface ClientNextI18Next {
  initialI18nStore: Record<string, Record<string, TranslationBundle>>;
  initialLocale: string;
  ns: TranslationFileName[];
  userConfig: typeof i18nextConfig;
}

const FALLBACK_LOCALES: Record<string, string[] | undefined> =
  (i18nextConfig.i18n as { fallbackLng?: Record<string, string[]> }).fallbackLng ?? {};

const getStoreLocales = (locale: string): string[] => [...new Set([locale, ...(FALLBACK_LOCALES[locale] ?? [])])];

const loadBundle = async (locale: string, namespace: TranslationFileName): Promise<TranslationBundle> => {
  const importBundle = TRANSLATION_BUNDLE_IMPORTS[`${locale}/${namespace}`];
  return importBundle ? (await importBundle()).default : {};
};

const loadLocaleBundles = async (
  locale: string,
  namespaces: TranslationFileName[]
): Promise<[string, Record<string, TranslationBundle>]> => {
  const bundles = await Promise.all(
    namespaces.map(async (namespace) => [namespace, await loadBundle(locale, namespace)] as const)
  );
  return [locale, Object.fromEntries(bundles)];
};

const buildNextI18Next = (
  initialLocale: string,
  ns: TranslationFileName[],
  initialI18nStore: ClientNextI18Next["initialI18nStore"]
): ClientNextI18Next => ({ initialI18nStore, initialLocale, ns, userConfig: i18nextConfig });

const getCacheKey = (locale: string, namespaces: TranslationFileName[]) => `${locale}|${namespaces.join(",")}`;

const loadedTranslations = new Map<string, ClientNextI18Next>();
const inflightLoads = new Map<string, Promise<ClientNextI18Next>>();

export const getLoadedClientTranslations = (
  locale: string,
  namespaces: TranslationFileName[]
): ClientNextI18Next | undefined => loadedTranslations.get(getCacheKey(locale, namespaces));

export const loadClientTranslations = (
  locale: string,
  namespaces: TranslationFileName[]
): Promise<ClientNextI18Next> => {
  const cacheKey = getCacheKey(locale, namespaces);
  const inflight = inflightLoads.get(cacheKey);
  if (inflight) {
    return inflight;
  }
  const load = Promise.all(getStoreLocales(locale).map((storeLocale) => loadLocaleBundles(storeLocale, namespaces)))
    .then((localeBundles) => {
      const translations = buildNextI18Next(locale, namespaces, Object.fromEntries(localeBundles));
      loadedTranslations.set(cacheKey, translations);
      return translations;
    })
    .finally(() => inflightLoads.delete(cacheKey));
  inflightLoads.set(cacheKey, load);
  return load;
};
